<template>
  <div
    :class="containerStyles"
    @click="toggle"
    @keydown.space.prevent="toggle"
    @keyup.enter="toggle"
    role="button"
    :aria-expanded="show"
    :aria-controls="'accordion-content-' + title"
    tabindex="0"
  >
    <div class="flex justify-between items-center">
      <div class="flex flex-col lg:flex-row lg:justify-between grow mr-5">
        <p :class="headerStyles">
          {{ title }}
        </p>
        <p v-if="tag" :class="[textColor, `text-sm font-bold mt-2 flex-1`]">
          {{ tag }}
        </p>
      </div>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="25"
        viewBox="0 0 24 25"
        fill="none"
        :class="['transition-transform w-6', show ? '-rotate-90' : 'rotate-0']"
      >
        <path
          d="M22 12.5L2 12.5"
          stroke-width="3"
          :class="[
            'transition duration-300 ease-in-out',
            themeType === `dark` ? 'stroke-white' : 'stroke-aubergine',
            show ? 'opacity-0' : 'opacity=100',
          ]"
        />
        <path
          d="M12 22.5L12 2.5"
          stroke-width="3"
          :class="[
            'transition duration-300 ease-in-out',
            themeType === `dark` ? 'stroke-white' : 'stroke-aubergine',
          ]"
        />
      </svg>
    </div>
    <Transition
      @before-enter="beforeEnter"
      @enter="enter"
      @before-leave="beforeLeave"
      @leave="leave"
    >
      <div class="h-auto overflow-hidden" v-show="show">
        <div class="inner" ref="innerRef">
          <slot></slot>
        </div>
      </div>
    </Transition>
  </div>
</template>
<script setup lang="ts">
import { computed, ref, toRefs } from "vue";
import { ThemeType } from "@/types/types";
const props = withDefaults(
  defineProps<{
    title: string;
    isUmbraco: boolean;
    themeType: ThemeType;
    classes?: string;
    tag?: string;
  }>(),
  {
    isUmbraco: false,
  }
);

const { isUmbraco, title, themeType, classes } = toRefs(props);

const show = ref(false);
const innerRef = ref<HTMLElement | null>(null);

const toggle = () => {
  show.value = !show.value;
};

function beforeEnter(el: Element) {
  if (!(el instanceof HTMLElement)) return;
  el.style.height = "0";
  if (!innerRef.value) return;
  innerRef.value.classList.remove("opacity-0", "translate-y-3");
  innerRef.value.classList.add("transition-all", "duration-300", "ease-in-out");
}
function enter(el: Element, done: () => void) {
  if (!(el instanceof HTMLElement)) return;
  requestAnimationFrame(() => {
    el.style.transition = "height 0.3s cubic-bezier(0.61, 1, 0.88, 1)";
    el.style.height = el.scrollHeight + "px";
    if (innerRef.value) {
      innerRef.value.classList.remove("translate-y-3");
      innerRef.value.classList.add("opacity-100");
    }
    el.addEventListener("transitionend", done, { once: true, passive: true });
  });
}
function beforeLeave(el: Element) {
  if (!(el instanceof HTMLElement)) return;
  el.style.height = el.scrollHeight + "px";
}

function leave(el: Element, done: () => void) {
  if (!(el instanceof HTMLElement)) return;
  requestAnimationFrame(() => {
    el.style.transition = "height 150ms ease-out";
    void el.offsetHeight; // trigger reflow
    el.style.height = "0";
    if (innerRef.value) {
      innerRef.value.classList.add("translate-y-3", "opacity-0");
    }
    el.addEventListener("transitionend", done, { once: true, passive: true });
  });
}

const containerStyles = computed(() => {
  const colorStyles = themeType.value === "dark" ? "border-white" : "border-aubergine";
  return `py-5 lg:py-7 w-full ${classes?.value} border-t-4 last:border-b-4 ${colorStyles} cursor-pointer`;
});

const textColor = computed(() => {
  return isUmbraco.value && themeType.value === "dark"
    ? "text-white"
    : isUmbraco.value && themeType.value === "light"
    ? "text-aubergine"
    : "text-typography";
});

const headerStyles = computed(() => {
  return `h4 ${textColor.value} flex-1`;
});
</script>
